/**
 * Color Palette System
 * 
 * A comprehensive color palette for consistent styling across the application.
 * Each color group follows a scale from 25 (lightest) to 900 (darkest).
 * 
 * Usage:
 * - 25-100: Backgrounds, hover states
 * - 200-300: Borders, disabled states
 * - 400-500: Primary usage, buttons, accents
 * - 600-900: Text, emphasis, dark mode accents
 */

export const colors = {
  purple: {
    25: '#F2EEFF',
    50: '#E2DAFF',
    100: '#C6B9FF',
    200: '#A896FF',
    300: '#8B74F2',
    400: '#735CE6',
    500: '#654EDA',
    600: '#654EDA',
  },
  blue: {
    25: '#EDF5FD',
    50: '#D6E8F9',
    100: '#B3D4F0',
    200: '#8CBDE7',
    300: '#65A6DE',
    400: '#4C98D9',
    500: '#3A8FD8',
    600: '#2E78B8',
    700: '#246296',
    800: '#1A4669',
    900: '#112E45',
  },
  teal: {
    25: '#EAF9F4',
    50: '#D2F2E8',
    100: '#AEE7D6',
    200: '#83D9C1',
    300: '#5DCCAE',
    400: '#46C2A4',
    500: '#34C4A1',
    600: '#2BA386',
    700: '#22836C',
    800: '#1A6454',
    900: '#0E3C31',
  },
  green: {
    25: '#ECF8EE',
    50: '#D6F0DA',
    100: '#B5E4BB',
    200: '#8FD596',
    300: '#69C870',
    400: '#55BF5F',
    500: '#4CBF5A',
    600: '#3FA04B',
    700: '#32803C',
    800: '#245A2B',
    900: '#183C1C',
  },
  yellow: {
    25: '#FFF8E6',
    50: '#FFF0C2',
    100: '#FFE599',
    200: '#FFD866',
    300: '#FACA3D',
    400: '#F5C531',
    500: '#F4C430',
    600: '#D9AA25',
    700: '#B58C1C',
    800: '#7D6013',
    900: '#523F0C',
  },
  orange: {
    25: '#FFF2E6',
    50: '#FFE0C2',
    100: '#FFC999',
    200: '#FFAF66',
    300: '#FA973D',
    400: '#F59630',
    500: '#F59A2E',
    600: '#D98124',
    700: '#B5671C',
    800: '#7D4713',
    900: '#52300C',
  },
  red: {
    25: '#FDECEC',
    50: '#F9D3D3',
    100: '#F3AAAA',
    200: '#ED7C7C',
    300: '#E85C5C',
    400: '#E84E4E',
    500: '#E84545',
    600: '#C73737',
    700: '#A02C2C',
    800: '#732020',
    900: '#4D1515',
  },
  magenta: {
    25: '#FCECF6',
    50: '#F7D2EB',
    100: '#EFA8D7',
    200: '#E57CBD',
    300: '#DD5BA8',
    400: '#D94CA0',
    500: '#D94BB3',
    600: '#BC3F97',
    700: '#99337A',
    800: '#6D2456',
    900: '#48173A',
  },
  gray: {
    25: '#F9FAFB',
    50: '#F3F4F6',
    100: '#E5E7EB',
    200: '#D1D5DB',
    300: '#9CA3AF',
    400: '#6B7280',
    500: '#4B5563',
    600: '#374151',
    700: '#1F2937',
    800: '#111827',
    900: '#030712',
  },
  cyan: {
    50: '#ECFEFF',
    100: '#CFFAFE',
    200: '#A5F3FC',
    300: '#67E8F9',
    400: '#22D3EE',
    500: '#06B6D4',
    600: '#0891B2',
    700: '#0E7490',
    800: '#155E75',
    900: '#164E63',
  },
  pink: {
    50: '#FDF2F8',
    100: '#FCE7F3',
    200: '#FBCFE8',
    300: '#F9A8D4',
    400: '#F472B6',
    500: '#EC4899',
    600: '#DB2777',
    700: '#BE185D',
    800: '#9D174D',
    900: '#831843',
  },
} as const;

/**
 * Chart Colors
 * 
 * A curated selection of colors optimized for data visualization.
 * These colors are selected from the 500 shade of each palette
 * to ensure good contrast and visual harmony.
 */
export const chartColors = {
  purple: colors.purple[500],
  blue: colors.blue[500],
  teal: colors.teal[500],
  green: colors.green[500],
  yellow: colors.yellow[500],
  orange: colors.orange[500],
  red: colors.red[500],
  magenta: colors.magenta[500],
  cyan: colors.cyan[500],
  pink: colors.pink[500],
  gray: colors.gray[300],
} as const;

/**
 * Chart Color Array
 * 
 * An ordered array of chart colors for sequential data series.
 * The order is designed to maximize visual distinction between adjacent colors.
 */
export const chartColorArray = [
  chartColors.purple,   // #654EDA
  chartColors.teal,     // #34C4A1
  chartColors.orange,   // #F59A2E
  chartColors.blue,     // #3A8FD8
  chartColors.magenta,  // #D94BB3
  chartColors.green,    // #4CBF5A
  chartColors.yellow,   // #F4C430
  chartColors.red,      // #E84545
  chartColors.cyan,     // #06B6D4
  chartColors.pink,     // #EC4899
] as const;

/**
 * Get a color from the palette
 * 
 * @param colorName - The color group name (e.g., 'purple', 'blue')
 * @param shade - The shade level (25, 50, 100, 200, 300, 400, 500, 600, 700, 800, 900)
 * @returns The hex color value
 */
export function getColor(
  colorName: keyof typeof colors,
  shade: keyof typeof colors.purple | keyof typeof colors.blue
): string {
  const colorGroup = colors[colorName];
  return (colorGroup as Record<number, string>)[shade] ?? colors.purple[500];
}

export type ColorName = keyof typeof colors;
export type ColorShade = 25 | 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900;

